import { Dialog, DialogPanel } from "lib/headlessui/components/dialog/dialog";
import {
    Transition,
    TransitionChild,
} from "lib/headlessui/components/transition/transition";
import React, { useCallback } from "react";
import { ResponsiveMode } from "store/reducers/responsive";
import { useResponsiveMode } from "store/selectors/hooks";
import { PanelType } from "./panel-type";
import { ResponsivePanelDesktopHeader } from "./responsive-panel-desktop-header";
import { ResponsivePanelMobileHeader } from "./responsive-panel-mobile-header";

interface ResponsivePanelProps {
    className?: string;
    isOpen: boolean;
    type: PanelType;
    headerText: string;
    allowOutsideDismiss?: boolean;
    noPadding?: boolean;
    onDismiss: () => void;
    children: React.ReactNode;
}
const widths = {
    [PanelType.smallFluid]: "max-w-md",
    [PanelType.smallFixedNear]: "max-w-md",
    [PanelType.medium]: "max-w-md",
    [PanelType.large]: "max-w-2xl",
    [PanelType.largeFixed]: "max-w-2xl",
    [PanelType.extraLarge]: "max-w-4xl",
};

export const ResponsivePanel: React.FC<ResponsivePanelProps> = props => {
    const {
        isOpen,
        headerText,
        onDismiss,
        type,
        allowOutsideDismiss = true,
        noPadding = false,
    } = props;
    const responsiveMode = useResponsiveMode();
    const panelWidth = widths[type];
    const isMobile =
        responsiveMode === ResponsiveMode.small ||
        responsiveMode === ResponsiveMode.medium;

    const onClose = useCallback(() => {
        if (allowOutsideDismiss) {
            onDismiss();
        }
    }, [allowOutsideDismiss, onDismiss]);

    return (
        <Transition appear={true} show={isOpen}>
            <Dialog as="div" className="relative z-1500" onClose={onClose}>
                <TransitionChild
                    enter={
                        isMobile ? "ease-out duration-300" : "ease-in-out duration-300"
                    }
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave={isMobile ? "ease-in duration-200" : "ease-in-out duration-500"}
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div
                            className={
                                isMobile
                                    ? "pointer-events-none fixed inset-0 flex"
                                    : "pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10"
                            }>
                            <TransitionChild
                                enter={
                                    isMobile
                                        ? "ease-out duration-300"
                                        : "transform transition ease-in-out duration-300 sm:duration-500"
                                }
                                enterFrom={isMobile ? "opacity-0" : "translate-x-full"}
                                enterTo={isMobile ? "opacity-100" : "translate-x-0"}
                                leave={
                                    isMobile
                                        ? "ease-in duration-200"
                                        : "transform transition ease-in-out duration-300 sm:duration-500"
                                }
                                leaveFrom={isMobile ? "opacity-100" : "translate-x-0"}
                                leaveTo={isMobile ? "opacity-0" : "translate-x-full"}>
                                <DialogPanel
                                    className={
                                        isMobile
                                            ? "pointer-events-auto fixed top-6 left-0 right-0 bottom-0 flex flex-col"
                                            : `pointer-events-auto w-screen ${panelWidth}`
                                    }>
                                    <div
                                        className={
                                            isMobile
                                                ? "flex h-full flex-col bg-white rounded-t-lg shadow-xl overflow-hidden"
                                                : "flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl"
                                        }>
                                        {isMobile ? (
                                            <ResponsivePanelMobileHeader
                                                headerText={headerText}
                                                onDismiss={onDismiss}
                                            />
                                        ) : (
                                            <ResponsivePanelDesktopHeader
                                                headerText={headerText}
                                                onDismiss={onDismiss}
                                            />
                                        )}
                                        <div
                                            className={
                                                noPadding
                                                    ? isMobile
                                                        ? "flex-1 overflow-y-auto"
                                                        : "relative flex-1"
                                                    : isMobile
                                                      ? "flex-1 overflow-y-auto px-4 py-6"
                                                      : "relative mt-6 flex-1 px-4 sm:px-6"
                                            }>
                                            <div className="app-component">
                                                <div className="panel-body">
                                                    {props.children}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
